import type { ReactiveController, ReactiveControllerHost } from 'lit'
import type { InputGroupControlState, InputGroupPresentationContextValue } from '../_InputGroupContext.js'
import { ContextConsumer } from '@lit/context'
import { inputGroupPresentationContext } from '../_InputGroupContext.js'

type InputGroupControlHost = ReactiveControllerHost & HTMLElement & {
  disabled: boolean
  readonly: boolean
  required: boolean
  error?: string
}

/**
 * Keeps the editable control's grouped presentation entirely inside its own
 * Shadow DOM. The composing parent receives public state snapshots and never
 * reads or writes the control's shadow root.
 */
export class InputGroupControlController implements ReactiveController {
  private context?: InputGroupPresentationContextValue
  private focusable?: HTMLElement
  private consumer: ContextConsumer<typeof inputGroupPresentationContext, InputGroupControlHost>

  constructor(
    private host: InputGroupControlHost,
    private getFocusable: () => HTMLElement | undefined,
  ) {
    host.addController(this)
    this.consumer = new ContextConsumer(host, {
      context: inputGroupPresentationContext,
      subscribe: true,
      callback: value => this.handleContext(value),
    })
  }

  get grouped() {
    return this.context !== undefined
  }

  get required() {
    return this.context?.required ?? false
  }

  get readonly() {
    return this.context?.readonly ?? false
  }

  get size() {
    return this.context?.size
  }

  get hasInlineStartAddon() {
    return this.context?.hasInlineStartAddon ?? false
  }

  get hasInlineEndAddon() {
    return this.context?.hasInlineEndAddon ?? false
  }

  requestContext() {
    this.consumer.hostConnected()
  }

  hostUpdated() {
    this.attachFocusable()
    this.report()
  }

  hostDisconnected() {
    this.detachFocusable()
    this.context?.unregisterControl(this.host)
    this.context = undefined
    this.host.requestUpdate()
  }

  private handleContext(value?: InputGroupPresentationContextValue) {
    const previous = this.context
    const ownerChanged = previous?.owner !== value?.owner
    if (ownerChanged) {
      previous?.unregisterControl(this.host)
    }

    this.context = value
    if (ownerChanged && value) {
      value.registerControl(this.host, this.snapshot())
    }
    this.host.requestUpdate()
    queueMicrotask(() => this.report())
  }

  private attachFocusable() {
    const focusable = this.getFocusable()
    if (focusable === this.focusable) {
      return
    }
    this.detachFocusable()
    this.focusable = focusable
    this.focusable?.addEventListener('focus', this.handleFocusChange)
    this.focusable?.addEventListener('blur', this.handleFocusChange)
  }

  private detachFocusable() {
    this.focusable?.removeEventListener('focus', this.handleFocusChange)
    this.focusable?.removeEventListener('blur', this.handleFocusChange)
    this.focusable = undefined
  }

  private handleFocusChange = () => {
    queueMicrotask(() => this.report())
  }

  private snapshot(): InputGroupControlState {
    return {
      disabled: this.host.disabled,
      readonly: this.host.readonly,
      required: this.host.required,
      invalid: Boolean(this.host.error),
      focusVisible: this.focusable?.matches(':focus-visible') ?? false,
    }
  }

  private report() {
    this.context?.updateControl(this.host, this.snapshot())
  }
}
